import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DelegationSessionResponse } from '@1po/1po-bff-fe-spec/generated/delegation/response/DelegationSessionResponse';
import { UserOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { Avatar } from 'antd-v5';
import { TFunction } from 'i18next';
import { RootState } from 'app/AppStore';
import { BriefcaseIcon, ExpandDownIcon, ExpandRightIcon } from 'assets/icons';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import {
  AccountListItem,
  fmt,
  ListAccounts,
} from 'components/Page/Header/TopHeader/Profile/DelegateSessionConnectionBox/ListAccounts';
import { SectionWrapper } from 'components/Page/Header/TopHeader/TopHeader.styled';
import {
  cancelCurrentDelegationSellerSaga,
  connectDelegationSessionRequest,
  getActiveSession,
  getActiveSessionStatus,
  getDelegationSellerSaga,
  getPossibleDelegateSessionBuyers,
  getPossibleDelegateSessionSellers,
  getPossibleDelegateSessionSellersData,
} from 'domains/delegation';
import { getDealerType } from 'domains/user';
import {
  useFetchBuyersForSellersForDelegationSession,
  useFetchSellersForDelegationSession,
} from 'domains/user/User.requests';
import { theme } from 'styles';
import { BlackButton, Flex, Icon, Input, MarginBox, Text, WhiteButton, YellowButton } from 'UI';
import { FOUND, isError, isLoading, isNotFound, LOADING, SEARCH_STATUS } from 'utils';

const getItemSectionTitle = (
  t: TFunction,
  delegationSession: DelegationSessionResponse | null,
  itemType: 'BUYER' | 'SELLER',
  selectedItem: string | undefined,
  items: AccountListItem[],
) => {
  const findItemByTresorCode = (tresorCode: string | undefined) =>
    tresorCode ? items.find((item) => item.tresorCode === tresorCode) : undefined;

  if (delegationSession) {
    const changedItem = findItemByTresorCode(selectedItem);
    if (findItemByTresorCode(selectedItem) && itemType) {
      return fmt(changedItem?.name, changedItem?.tresorCode);
    } else {
      return itemType === 'BUYER'
        ? fmt(delegationSession.delegatedBuyerName, delegationSession.delegatedBuyerTresorCode)
        : fmt(delegationSession.delegatedSellerTresorCode, delegationSession.delegatedSellerTresorCode);
    }
  }

  const currItem = findItemByTresorCode(selectedItem);
  if (currItem) {
    return fmt(currItem.name, currItem.tresorCode, currItem.primaryClientCode);
  }
  return itemType === 'BUYER'
    ? t('connect_as.client.identify.title', 'Identify  R2/R3 Garage')
    : t('connect_as.dealer.identify.title', 'Identify  R1 Garage');
};

const getItemSectionDescription = (t: TFunction, itemType: 'BUYER' | 'SELLER') =>
  itemType === 'BUYER'
    ? t('connect_as.client.identify.description', 'Use garage’s ID Tresor or BIR Code')
    : t('connect_as.dealer.identify.description', 'Use Commercial name, Id Tresor or BIR Code');

const resolveSectionTitleStatus = (
  sessionStatus: SEARCH_STATUS,
  activeSession: DelegationSessionResponse | null,
  dataStatus: SEARCH_STATUS,
) => {
  if (isLoading(sessionStatus)) {
    return sessionStatus;
  }
  if (activeSession) {
    return FOUND;
  }
  return dataStatus;
};

const normalizeSearchStatus = (searchStatus: SEARCH_STATUS | null | undefined): SEARCH_STATUS =>
  searchStatus ?? undefined;

const toggleSectionSelector = (
  dataLength: number,
  hasActiveSession: boolean,
  expandDataSelector: boolean,
  setExpandDataSelector: (val: boolean) => void,
) => {
  if (dataLength > 1 && !hasActiveSession) {
    setExpandDataSelector(!expandDataSelector);
  }
};

const initializeSectionSelection = (
  selectedItem: string | undefined,
  initialExpandDone: boolean,
  tresorCodes: string[],
  inline: boolean,
  setSelectedItem: (val: string) => void,
  setExpandDataSelector: (val: boolean) => void,
  setInitialExpandDone: (val: boolean) => void,
) => {
  if (selectedItem || initialExpandDone || tresorCodes.length === 0) {
    return;
  }

  if (tresorCodes.length === 1) {
    setSelectedItem(tresorCodes[0]);
    setExpandDataSelector(false);
  } else if (inline) {
    // Explicit no-op branch keeps the initialization flow exhaustive.
  } else {
    setExpandDataSelector(true);
  }

  setInitialExpandDone(true);
};

interface SectionProps {
  itemType: 'BUYER' | 'SELLER';
  dataStatus: SEARCH_STATUS;
  data: AccountListItem[];
  selectedItem: string | undefined;
  setSelectedItem: (val: string) => void;
  expandDataSelector: boolean;
  setExpandDataSelector: (val: boolean) => void;
  inline: boolean;
  dropDownInputs: boolean;
  pointer?: boolean;
  handleOnCancel?: () => void;
}

interface SectionHeaderProps {
  titleStatus: SEARCH_STATUS;
  inline: boolean;
  title: string;
  description: string;
  dropDownInputs: boolean;
  expandDataSelector: boolean;
  pointer: boolean;
  selectedItem: string | undefined;
  tresorCodesLength: number;
  activeSession: DelegationSessionResponse | null;
  handleOnCancel?: () => void;
  handleCancelClick: () => void;
  handleToggleSelector: () => void;
  cancelLabel: string;
}

const SectionHeader = ({
  titleStatus,
  inline,
  title,
  description,
  dropDownInputs,
  expandDataSelector,
  pointer,
  selectedItem,
  handleToggleSelector,
}: SectionHeaderProps) => {
  const displayIcon = !dropDownInputs && selectedItem;

  const renderHeaderLabels = () => (
    <Flex direction={'column'}>
      <Text type="base" color="grey35" noWrap={dropDownInputs} ellipsis>
        {title}
      </Text>
      {!inline && (
        <Text type="base" color="grey65">
          {description}
        </Text>
      )}
    </Flex>
  );

  return (
    <SectionWrapper
      minHeight={dropDownInputs ? 36 : 50}
      maxHeight={76}
      align={'center'}
      onClick={handleToggleSelector}
      pointer={pointer}
      dropDownInputs={dropDownInputs}
      data-cy={`connect-as-item-${title}`}
      padding={`0 20px 0 ${displayIcon ? '0' : '55px'}`}
    >
      {displayIcon && (
        <Flex padding={'0 16px'} size={0}>
          <Avatar size={44} icon={<UserOutlined />} />
        </Flex>
      )}
      <Flex direction={'column'} maxWidth={inline ? 320 : undefined}>
        <DataContainer
          data={titleStatus}
          ErrorState={() => <ErrorWithLabel narrow={inline} />}
          Skeleton={() => renderHeaderLabels()}
        >
          {renderHeaderLabels()}
        </DataContainer>
      </Flex>
      <Flex size={0}>
        <Icon IconComponent={expandDataSelector ? ExpandDownIcon : ExpandRightIcon} size={24} noPointer={!pointer} />
      </Flex>
    </SectionWrapper>
  );
};

const Section = ({
  itemType,
  dataStatus,
  data,
  selectedItem,
  setSelectedItem,
  expandDataSelector,
  setExpandDataSelector,
  inline,
  dropDownInputs,
  pointer = false,
  handleOnCancel,
}: SectionProps) => {
  const { t } = useTranslation();
  const activeSession = useSelector(getActiveSession);
  const sessionStatus = useSelector(getActiveSessionStatus);
  const titleStatus = resolveSectionTitleStatus(
    normalizeSearchStatus(sessionStatus),
    activeSession,
    normalizeSearchStatus(dataStatus),
  );
  const tresorCodes = useMemo(() => data.map((s) => s.tresorCode), [data]);
  const title = getItemSectionTitle(t, activeSession, itemType, selectedItem, data);
  const description = getItemSectionDescription(t, itemType);
  const [initialExpandDone, setInitialExpandDone] = useState(false);

  const handleToggleSelector = useCallback(() => {
    toggleSectionSelector(data.length, Boolean(activeSession), expandDataSelector, setExpandDataSelector);
  }, [activeSession, data.length, expandDataSelector, setExpandDataSelector]);

  const handleCancelClick = useCallback(() => handleOnCancel?.(), [handleOnCancel]);

  const handleSelectItem = useCallback(
    (item: AccountListItem) => {
      setSelectedItem(item.tresorCode);
      setExpandDataSelector(false);
    },
    [setExpandDataSelector, setSelectedItem],
  );

  useEffect(() => {
    initializeSectionSelection(
      selectedItem,
      initialExpandDone,
      tresorCodes,
      inline,
      setSelectedItem,
      setExpandDataSelector,
      setInitialExpandDone,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tresorCodes]);

  const SelectBox = ({ children }: { children: React.ReactNode }) => {
    if (inline) {
      return <Popover placement={'bottom'} visible={expandDataSelector} content={children} />;
    }
    return children;
  };

  return (
    <Flex direction={'column'} data-cy={`client-selection-${title}`}>
      <SectionHeader
        titleStatus={titleStatus}
        inline={inline}
        title={title}
        description={description}
        dropDownInputs={dropDownInputs}
        expandDataSelector={expandDataSelector}
        pointer={pointer}
        selectedItem={selectedItem}
        tresorCodesLength={tresorCodes.length}
        activeSession={activeSession}
        handleOnCancel={handleOnCancel}
        handleCancelClick={handleCancelClick}
        handleToggleSelector={handleToggleSelector}
        cancelLabel={t('common.action.cancel', 'Cancel')}
      />
      {expandDataSelector && (
        <Flex background={theme.color.primary_brand_active} minHeight={212}>
          <SelectBox>
            <ListAccounts
              data={data}
              data-cy={'client-list'}
              key={`${itemType}-clientList`}
              labelInputPlaceholder={
                itemType === 'BUYER'
                  ? t('connect_as.client_search', `Search for client here...`)
                  : t('connect_as.dealer_search', `Search for dealer here...`)
              }
              onSelect={handleSelectItem}
              notFoundLabel={
                itemType === 'BUYER'
                  ? t('connect_as.dealer_search_not_found', `R2/3 Not found`)
                  : t('connect_as.client_search_not_found', `R1 Not found`)
              }
            />
          </SelectBox>
        </Flex>
      )}
    </Flex>
  );
};

interface SellerSectionProps {
  selectedSellerTresorCode: string | undefined;
  setSelectedSellerTresorCode: (val: string | undefined) => void;
  expandSellersSelector: boolean;
  setExpandSellersSelector: (val: boolean) => void;
  setExpandBuyersSelector: (val: boolean) => void;
  inline: boolean;
  dropDownInputs: boolean;
  clickable: boolean;
}

const SellerSection = ({
  selectedSellerTresorCode,
  setSelectedSellerTresorCode,
  expandSellersSelector,
  setExpandSellersSelector,
  setExpandBuyersSelector,
  inline,
  dropDownInputs,
  clickable,
}: SellerSectionProps) => {
  const isR1 = useSelector(getDealerType) === 'R1';

  if (isR1) {
    return (
      <SellerSelector
        selectedSeller={selectedSellerTresorCode}
        setSelectedSeller={setSelectedSellerTresorCode}
        expandSellersSelector={expandSellersSelector}
        setExpandSellersSelector={setExpandSellersSelector}
        inline={inline}
        dropDownInputs={dropDownInputs}
        clickable={clickable}
      />
    );
  }

  return (
    <SellerSearchBox
      selectedSeller={selectedSellerTresorCode}
      setSelectedSeller={setSelectedSellerTresorCode}
      expandSellersSelector={expandSellersSelector}
      setExpandSellersSelector={setExpandSellersSelector}
      setExpandBuyersSelector={setExpandBuyersSelector}
      inline={inline}
      dropDownInputs={dropDownInputs}
      clickable={clickable}
    />
  );
};

interface SellerSearchBoxProps {
  selectedSeller: string | undefined;
  setSelectedSeller: (val: string | undefined) => void;
  expandSellersSelector: boolean;
  setExpandSellersSelector: (val: boolean) => void;
  setExpandBuyersSelector: (val: boolean) => void;
  inline: boolean;
  dropDownInputs: boolean;
  clickable: boolean;
}

const SellerSearchBox = ({
  selectedSeller,
  setSelectedSeller,
  expandSellersSelector,
  setExpandSellersSelector,
  setExpandBuyersSelector,
  inline,
  dropDownInputs,
  clickable,
}: SellerSearchBoxProps) => {
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sellers = useSelector(getPossibleDelegateSessionSellers);
  const activeSession = useSelector(getActiveSession);
  const sellersStatus = sellers?.searchStatus;
  const inputStatus = isError(sellersStatus) || isNotFound(sellersStatus) ? 'error' : undefined;

  useEffect(() => {
    if (!activeSession && !selectedSeller) {
      if (Array.isArray(sellers?.data)) {
        setSelectedSeller(sellers.data[0].tresorCode);
        setExpandBuyersSelector(true);
      } else {
        setSelectedSeller(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellersStatus]);

  const sendGetDelegationSeller = () => {
    if (inputValue.length !== 8) {
      return;
    }
    dispatch(getDelegationSellerSaga(inputValue));
  };

  const removeDelegationSeller = () => {
    dispatch(cancelCurrentDelegationSellerSaga());
    setExpandBuyersSelector(false);
    setSelectedSeller(undefined);
  };

  if (sellers?.data) {
    return (
      <SellerSelector
        selectedSeller={selectedSeller}
        setSelectedSeller={setSelectedSeller}
        expandSellersSelector={expandSellersSelector}
        setExpandSellersSelector={setExpandSellersSelector}
        inline={inline}
        dropDownInputs={dropDownInputs}
        clickable={clickable}
        handleOnCancel={() => removeDelegationSeller()}
      />
    );
  }

  return (
    <Flex direction={'column'}>
      <SectionWrapper minHeight={50} align={'center'} dropDownInputs={dropDownInputs}>
        {!dropDownInputs && (
          <MarginBox ml={21} mr={16}>
            <Icon IconComponent={BriefcaseIcon} size={32} display={'inline'} color={theme.color.grey00} noPointer />
          </MarginBox>
        )}
        <Flex minWidth={225} maxWidth={inline ? 520 : 320}>
          <Flex size={2}>
            <Input
              onChange={(val) => {
                const numericOnly = val.replace(/\D/g, '');
                setInputValue(numericOnly);
              }}
              value={inputValue}
              placeholder={t('connect_as.enter_r1_bir_code_form.placeholder', 'Enter R1 BIR code')}
              maxLength={8}
              disabled={selectedSeller !== undefined}
              bordered
              status={inputStatus}
            />
          </Flex>
          <MarginBox ml={15} />
          <Flex>
            <DataContainer data={resolveConnectAsButtonDisplayStatus(normalizeSearchStatus(sellersStatus))}>
              <YellowButton
                disabled={inputValue.length !== 8}
                stretch={false}
                size={'middle'}
                onClick={() => sendGetDelegationSeller()}
              >
                {t('connect_as.enter_r1_bir_code_form.validate_button_label', 'Validate')}
              </YellowButton>
            </DataContainer>
          </Flex>
        </Flex>
      </SectionWrapper>
    </Flex>
  );
};

interface SellerSelectorProps {
  selectedSeller: string | undefined;
  setSelectedSeller: (val: string) => void;
  expandSellersSelector: boolean;
  setExpandSellersSelector: (val: boolean) => void;
  inline: boolean;
  dropDownInputs: boolean;
  clickable: boolean;
  handleOnCancel?: () => void;
}

const SellerSelector = ({
  selectedSeller,
  setSelectedSeller,
  expandSellersSelector,
  setExpandSellersSelector,
  inline,
  dropDownInputs,
  clickable,
  handleOnCancel,
}: SellerSelectorProps) => {
  const sellersSearchData = useSelector(getPossibleDelegateSessionSellersData);
  const sellers = sellersSearchData?.data ?? [];

  return (
    <Section
      itemType={'SELLER'}
      dataStatus={normalizeSearchStatus(sellersSearchData?.searchStatus)}
      data={sellers}
      selectedItem={selectedSeller}
      setSelectedItem={setSelectedSeller}
      expandDataSelector={expandSellersSelector}
      setExpandDataSelector={setExpandSellersSelector}
      inline={inline}
      dropDownInputs={dropDownInputs}
      pointer={clickable && sellers?.length > 1}
      handleOnCancel={handleOnCancel}
    />
  );
};

interface BuyerSelectorProps {
  selectedSellerTresorCode: string | undefined;
  selectedBuyerTresorCode: string | undefined;
  setSelectedBuyerTresorCode: (val: string) => void;
  expandBuyersSelector: boolean;
  setExpandBuyersSelector: (val: boolean) => void;
  inline: boolean;
  dropDownInputs: boolean;
  pointer: boolean;
}

const BuyerSelector = ({
  selectedSellerTresorCode,
  selectedBuyerTresorCode,
  setSelectedBuyerTresorCode,
  expandBuyersSelector,
  setExpandBuyersSelector,
  inline,
  dropDownInputs,
  pointer,
}: BuyerSelectorProps) => {
  const buyersSearchData = useSelector((state: RootState) =>
    getPossibleDelegateSessionBuyers(state, selectedSellerTresorCode),
  );
  const buyers = buyersSearchData?.data ?? [];

  return (
    <Section
      itemType={'BUYER'}
      dataStatus={normalizeSearchStatus(buyersSearchData?.searchStatus)}
      data={buyers}
      selectedItem={selectedBuyerTresorCode}
      setSelectedItem={setSelectedBuyerTresorCode}
      expandDataSelector={expandBuyersSelector}
      setExpandDataSelector={setExpandBuyersSelector}
      inline={inline}
      dropDownInputs={dropDownInputs}
      pointer={pointer}
    />
  );
};

/**
 * Display connect as button state based on session status.
 * - LOADING: Show loading spinner while connecting
 * - FOUND: Hide button (session is active)
 * - NOT_FOUND or undefined: Show button (ready to connect)
 */
const resolveConnectAsButtonDisplayStatus = (sessionStatus: SEARCH_STATUS): SEARCH_STATUS => {
  if (isLoading(sessionStatus)) {
    return LOADING;
  }
  if (sessionStatus === FOUND) {
    return FOUND;
  }
  return FOUND;
};

interface DelegateSessionConnectionBoxBodyProps {
  selectedSellerTresorCode: string | undefined;
  setSelectedSeller: (val: string | undefined) => void;
  selectedBuyerTresorCode: string | undefined;
  setSelectedBuyerTresorCode: (val: string | undefined) => void;
  expandSellersSelector: boolean;
  setExpandSellersSelector: (val: boolean) => void;
  expandBuyersSelector: boolean;
  setExpandBuyersSelector: (val: boolean) => void;
  clickable: boolean;
  inline?: boolean;
  dropDownInputs?: boolean;
  onBack?: () => void;
}

export const DelegateSessionConnectionBoxBody = ({
  selectedSellerTresorCode,
  setSelectedSeller,
  selectedBuyerTresorCode,
  setSelectedBuyerTresorCode,
  expandSellersSelector,
  setExpandSellersSelector,
  expandBuyersSelector,
  setExpandBuyersSelector,
  inline = false,
  dropDownInputs = false,
  clickable,
  onBack = () => undefined,
}: DelegateSessionConnectionBoxBodyProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sessionStatus = useSelector(getActiveSessionStatus);
  const activeSession = useSelector(getActiveSession);
  useFetchSellersForDelegationSession();
  useFetchBuyersForSellersForDelegationSession(selectedSellerTresorCode);

  const selectSeller = (newSellerTresorCode: string | undefined) => {
    if (selectedSellerTresorCode !== newSellerTresorCode) {
      setSelectedBuyerTresorCode(undefined);
    }
    setSelectedSeller(newSellerTresorCode);
  };

  const handleConnectToDelegateSession = useCallback(
    (sellerId: string, buyerId: string) => {
      dispatch(
        connectDelegationSessionRequest({
          delegatedBuyerTresorCode: buyerId,
          delegatedSellerTresorCode: sellerId,
          delegatedBuyerName: '',
        }),
      );
      setExpandSellersSelector(false);
      setExpandBuyersSelector(false);
    },
    [dispatch, setExpandBuyersSelector, setExpandSellersSelector],
  );

  const hasSelection = Boolean(selectedSellerTresorCode && selectedBuyerTresorCode);
  const canUseConnectionButton = !inline && clickable;
  const noActiveSession = !activeSession?.active;
  const showConnectButton = canUseConnectionButton && noActiveSession;

  const handleConnectClick = useCallback(() => {
    if (selectedSellerTresorCode && selectedBuyerTresorCode) {
      handleConnectToDelegateSession(selectedSellerTresorCode, selectedBuyerTresorCode);
    }
  }, [handleConnectToDelegateSession, selectedBuyerTresorCode, selectedSellerTresorCode]);

  return (
    <Flex direction={'column'}>
      <Flex direction={'column'}>
        <Flex size={0} direction={inline ? 'row' : 'column'} gap={inline ? 15 : 0} data-cy={'connect-as-section'}>
          <SellerSection
            selectedSellerTresorCode={selectedSellerTresorCode}
            setSelectedSellerTresorCode={selectSeller}
            expandSellersSelector={expandSellersSelector}
            setExpandSellersSelector={setExpandSellersSelector}
            setExpandBuyersSelector={setExpandBuyersSelector}
            inline={inline}
            dropDownInputs={dropDownInputs}
            clickable={clickable}
          />
          <BuyerSelector
            selectedSellerTresorCode={selectedSellerTresorCode}
            selectedBuyerTresorCode={selectedBuyerTresorCode}
            setSelectedBuyerTresorCode={setSelectedBuyerTresorCode}
            expandBuyersSelector={expandBuyersSelector}
            setExpandBuyersSelector={setExpandBuyersSelector}
            inline={inline}
            dropDownInputs={dropDownInputs}
            pointer={clickable}
          />
        </Flex>
      </Flex>

      {showConnectButton && (
        <DataContainer data={resolveConnectAsButtonDisplayStatus(normalizeSearchStatus(sessionStatus))}>
          <Flex minHeight={70} maxHeight={70} align={'center'} justify={'flex-end'} gap={15} padding={'0 12px'}>
            <WhiteButton outlined shape={'semiRound'} onClick={onBack}>
              {t('common.action.cancel', 'Cancel')}
            </WhiteButton>
            <BlackButton
              data-cy={'connect-as-button'}
              onClick={handleConnectClick}
              disabled={isLoading(normalizeSearchStatus(sessionStatus)) || !hasSelection}
              shape={'semiRound'}
            >
              {isLoading(normalizeSearchStatus(sessionStatus))
                ? t('connect_as.connecting', 'Connecting...')
                : t('connect_as.confirm_connect_as', 'Connect as')}
            </BlackButton>
          </Flex>
        </DataContainer>
      )}
    </Flex>
  );
};
